import React, { useMemo } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import CommerceTreeNode from './CommerceTreeNode'

const styles = {
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 4 },
  groupTitle: { fontWeight: 700, mt: 2, mb: 0.5 },
  groupHint: { color: 'text.secondary', display: 'block', mb: 1 },
}

// Mismo orden que la pantalla de comercios; un tipo que no reconozcamos va al final
const ORDEN_TIPO = {
  H2H: 1,
  DISTRIBUIDOR: 2,
  MENUDEO: 3,
}

const idOf = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') return String(value._id || value.id || '')
  return String(value)
}

/**
 * Arma la jerarquia a partir de la lista plana.
 * Devuelve las raices (sin comercio padre) y los huerfanos: los que declaran un
 * padre que no viene en la lista. Sin esto esos comercios no se verian en ningun
 * sitio, que es lo que pasa hoy en la pantalla de comercios.
 */
export const buildTree = (comercios) => {
  const list = Array.isArray(comercios) ? comercios.filter((item) => item && idOf(item._id)) : []

  const byId = new Map()
  list.forEach((item) => byId.set(idOf(item._id), { ...item, _id: idOf(item._id), children: [] }))

  const roots = []
  const orphans = []

  byId.forEach((node) => {
    const parentId = idOf(node.hosterOwner)
    if (!parentId) {
      roots.push(node)
      return
    }
    const parent = byId.get(parentId)
    if (!parent) {
      orphans.push(node)
      return
    }
    // Un comercio que se declara padre de si mismo colgaria el render
    if (parent._id === node._id) {
      orphans.push(node)
      return
    }
    parent.children.push(node)
  })

  const sort = (nodes) => {
    nodes.sort((a, b) => {
      const tipoA = ORDEN_TIPO[a.type_commerce] || 99
      const tipoB = ORDEN_TIPO[b.type_commerce] || 99
      if (tipoA !== tipoB) return tipoA - tipoB
      return String(a.username || '').localeCompare(String(b.username || ''), 'es')
    })
    nodes.forEach((node) => sort(node.children))
  }
  sort(roots)
  sort(orphans)

  return { roots, orphans }
}

const matches = (node, { search, state, type }) => {
  if (typeof state === 'boolean' && node.state !== state) return false
  if (type && node.type_commerce !== type) return false
  if (!search) return true

  const needle = String(search).trim().toLowerCase()
  if (!needle) return true

  return [node.username, node.identification, node?.user?.email]
    .filter((value) => value !== undefined && value !== null)
    .some((value) => String(value).toLowerCase().includes(needle))
}

/**
 * Poda el arbol dejando los nodos que coinciden y la cadena de padres que lleva
 * hasta ellos. Devuelve tambien los ids de esos padres para dejarlos expandidos.
 */
const prune = (nodes, filters, forced, visited) => (
  nodes.reduce((kept, node) => {
    // Guarda contra un ciclo en los datos: sin esto el render no termina
    if (visited.has(node._id)) return kept
    visited.add(node._id)

    const children = prune(node.children, filters, forced, visited)
    const self = matches(node, filters)

    if (!self && children.length === 0) return kept

    if (children.length > 0) forced[node._id] = true

    // El nodo que coincide conserva su descendencia entera, para poder explorarla
    kept.push({ ...node, children: self && children.length === 0 ? node.children : children })
    return kept
  }, [])
)

const CommerceTree = ({
  comercios,
  loading = false,
  error = '',
  filters = {},
  expanded = {},
  actions = [],
  onToggle,
}) => {
  const { roots, orphans } = useMemo(() => buildTree(comercios), [comercios])

  const search = String(filters.search || '').trim()

  const hasFilters = Boolean(search || typeof filters.state === 'boolean' || filters.type)

  const { visibleRoots, visibleOrphans, forced } = useMemo(() => {
    if (!hasFilters) return { visibleRoots: roots, visibleOrphans: orphans, forced: {} }
    const forcedIds = {}
    return {
      visibleRoots: prune(roots, filters, forcedIds, new Set()),
      visibleOrphans: prune(orphans, filters, forcedIds, new Set()),
      forced: forcedIds,
    }
  }, [roots, orphans, filters, hasFilters])

  // Sólo la búsqueda abre ramas sola: es cuando se persigue un comercio concreto.
  // Los filtros recortan, pero dejan el árbol como lo tenga el usuario — si no,
  // el filtro por defecto de «sólo activos» lo abriría entero al entrar.
  const openState = search ? { ...expanded, ...forced } : expanded

  if (loading) {
    return (
      <Box sx={styles.center}>
        <CircularProgress size={20} />
        <Typography variant="body2">Cargando la estructura de comercios…</Typography>
      </Box>
    )
  }

  if (error) return <Alert severity="error">{error}</Alert>

  if (visibleRoots.length === 0 && visibleOrphans.length === 0) {
    return (
      <Box sx={styles.center}>
        <Typography variant="body2">
          {search ? 'Ningún comercio coincide con la búsqueda.' : hasFilters ? 'Ningún comercio coincide con los filtros.' : 'No hay comercios que mostrar.'}
        </Typography>
      </Box>
    )
  }

  const node = (item) => (
    <CommerceTreeNode
      key={item._id}
      node={item}
      level={0}
      expanded={openState}
      actions={actions}
      onToggle={onToggle}
    />
  )

  return (
    <Box>
      {visibleRoots.map(node)}

      {visibleOrphans.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={styles.groupTitle}>
            Sin comercio padre
          </Typography>
          <Typography variant="caption" sx={styles.groupHint}>
            Estos comercios apuntan a un comercio padre que ya no existe.
          </Typography>
          {visibleOrphans.map(node)}
        </Box>
      )}
    </Box>
  )
}

export default CommerceTree
